/**
 * Shared transaction form modal — used by the global FAB,
 * the SpendingCalendar day-cell click, and the Transactions log.
 */
import { AnimatePresence, motion } from "framer-motion";
import { useLiveQuery } from "dexie-react-hooks";
import {
  ArrowDownRight,
  ArrowUpRight,
  Calendar,
  CreditCard,
  FolderClosed,
  Plus,
  Receipt,
  Repeat,
  Tag,
  User,
  X,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { cn, generateId } from "@repo/core";
import { useCategories } from "../hooks/useBudgetData";
import { useCurrency } from "../hooks/useCurrency";
import { accountTypeLabel, categoryLabel, personCanonical, personLabel, PERSON_CANONICAL, isKnownPersonPreset } from "../i18n/labels";
import { useT } from "../i18n/useT";
import { db } from "../lib/db";
import { handleFormSubmit, onEnterToSubmitKeyDown } from "../lib/formKeyboard";
import {
  buildRecurringSeries,
  detachOccurrence,
  seriesUpdatePayload,
} from "../lib/recurring";
import type { RecurringFrequency, Transaction } from "../lib/schema";

interface Props {
  open: boolean;
  onClose: () => void;
  defaultDate?: string;
  initialData?: Transaction | null;
  onSuccess?: () => void;
}

export function TransactionModal({
  open,
  onClose,
  defaultDate,
  initialData,
  onSuccess,
}: Props) {
  const today = new Date().toISOString().split("T")[0];
  const { t } = useT();
  const { symbol } = useCurrency();
  const categories = useCategories();
  const accounts = useLiveQuery(() => db.accounts.toArray(), []) ?? [];

  const personPresets = useMemo(
    () => [
      { value: PERSON_CANONICAL.household, label: t("person.household") },
      { value: PERSON_CANONICAL.personal, label: t("person.personal") },
      { value: PERSON_CANONICAL.partner, label: t("person.partner") },
    ],
    [t]
  );
  const defaultPerson = PERSON_CANONICAL.household;
  const preferredAccountId =
    accounts.find((a) => a.type === "checking")?.id ?? accounts[0]?.id ?? "";

  const [form, setForm] = useState({
    date: defaultDate ?? today,
    type: "expense" as Transaction["type"],
    amount: "",
    categoryId: "",
    accountId: "",
    person: defaultPerson as string,
    notes: "",
    recurring: false,
    frequency: "monthly" as RecurringFrequency,
    intervalDays: "14",
    seriesScope: "one" as "one" | "series",
  });

  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initialData) {
      setForm({
        date: initialData.date,
        type: initialData.type,
        amount: String(initialData.amount),
        categoryId: initialData.categoryId,
        accountId: initialData.accountId,
        person: initialData.person || defaultPerson,
        notes: initialData.notes || "",
        recurring: !!initialData.seriesId,
        frequency: initialData.recurringFrequency ?? "monthly",
        intervalDays: String(initialData.recurringIntervalDays ?? 14),
        seriesScope: "one",
      });
    } else {
      setForm({
        date: defaultDate ?? today,
        type: "expense",
        amount: "",
        categoryId: "",
        accountId: preferredAccountId,
        person: defaultPerson,
        notes: "",
        recurring: false,
        frequency: "monthly",
        intervalDays: "14",
        seriesScope: "one",
      });
    }
  }, [open, initialData, defaultDate, today, preferredAccountId, defaultPerson]);

  // Relevant categories for selected type
  const relevantCats =
    form.type === "income"
      ? categories.filter((c) => c.type === "income")
      : categories.filter((c) => c.type === "expense");

  // Auto-select checking (or first) account if empty and available
  useEffect(() => {
    if (!open) return;
    if (!form.accountId && preferredAccountId) {
      setForm((f) => ({ ...f, accountId: preferredAccountId }));
    }
  }, [open, preferredAccountId, form.accountId]);

  const handleTypeChange = (newType: Transaction["type"]) => {
    const cats =
      newType === "income"
        ? categories.filter((c) => c.type === "income")
        : categories.filter((c) => c.type === "expense");

    setForm((f) => ({
      ...f,
      type: newType,
      categoryId: cats[0]?.id ?? "",
    }));
  };

  const handleSave = async () => {
    const amt = parseFloat(form.amount);
    if (isNaN(amt) || amt <= 0 || !form.categoryId) return;
    if (accounts.length > 0 && !form.accountId) return;

    const accountId = form.accountId || "";
    const person = personCanonical(form.person || defaultPerson, t);

    setBusy(true);
    try {
      if (initialData?.id) {
        const base = {
          date: form.date,
          type: form.type,
          amount: amt,
          categoryId: form.categoryId,
          accountId,
          person,
          notes: form.notes.trim(),
        };

        if (initialData.seriesId && form.seriesScope === "series") {
          const siblings = await db.transactions
            .where("seriesId")
            .equals(initialData.seriesId)
            .toArray();
          await Promise.all(
            siblings
              .filter((row) => !row.skipped)
              .map((row) =>
                db.transactions.update(row.id, seriesUpdatePayload(base))
              )
          );
        } else if (initialData.seriesId && form.seriesScope === "one") {
          await db.transactions.update(initialData.id, {
            ...base,
            ...detachOccurrence(initialData),
          });
        } else {
          await db.transactions.update(initialData.id, base);
        }
      } else if (form.recurring) {
        const series = buildRecurringSeries({
          date: form.date,
          type: form.type,
          amount: amt,
          categoryId: form.categoryId,
          accountId,
          person,
          notes: form.notes.trim(),
          frequency: form.frequency,
          intervalDays: parseInt(form.intervalDays, 10) || 14,
        });
        await db.transactions.bulkAdd(series);
      } else {
        await db.transactions.add({
          id: generateId(),
          date: form.date,
          type: form.type,
          amount: amt,
          categoryId: form.categoryId,
          accountId,
          person,
          notes: form.notes.trim(),
        });
      }
      onSuccess?.();
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const handleSkipOccurrence = async () => {
    if (!initialData?.id || !initialData.seriesId) return;
    if (!confirm(t("txModal.skipConfirm"))) return;
    setBusy(true);
    try {
      await db.transactions.update(initialData.id, { skipped: true });
      onSuccess?.();
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const set = (k: string, v: string | boolean) => setForm((f) => ({ ...f, [k]: v }));
  const canSave =
    !isNaN(parseFloat(form.amount)) &&
    parseFloat(form.amount) > 0 &&
    !!form.categoryId &&
    (accounts.length === 0 || !!form.accountId);

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 bg-black/55 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Modal Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 14 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.94, y: 14 }}
            transition={{ type: "spring", damping: 26, stiffness: 360 }}
            className="relative w-full max-w-lg bg-card border border-border/80 rounded-2xl shadow-2xl overflow-hidden z-10 flex flex-col max-h-[92vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-border/60 bg-muted/20">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center shadow-xs">
                  {initialData ? <Receipt size={20} /> : <Plus size={20} />}
                </div>
                <div>
                  <h2
                    className="text-lg font-semibold text-foreground tracking-tight leading-none"
                    style={{ fontFamily: "var(--font-display, inherit)" }}
                  >
                    {initialData ? t("txModal.edit") : t("txModal.new")}
                  </h2>
                  <p className="text-xs text-muted-foreground mt-1">
                    {initialData ? t("txModal.editSub") : t("txModal.newSub")}
                  </p>
                </div>
              </div>

              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors"
                aria-label={t("common.close")}
              >
                <X size={16} />
              </button>
            </div>

            {/* Form Scrollable Body + footer */}
            <form
              className="flex flex-col flex-1 min-h-0"
              onKeyDown={onEnterToSubmitKeyDown}
              onSubmit={(e) => handleFormSubmit(e, () => handleSave())}
            >
            <div className="p-6 overflow-y-auto space-y-5">
              {/* Type Switcher (Segmented Pill) */}
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1.5">
                  {t("txModal.type")}
                </label>
                <div className="grid grid-cols-3 gap-1.5 p-1 bg-muted/50 rounded-xl border border-border/60">
                  <button
                    type="button"
                    onClick={() => handleTypeChange("expense")}
                    className={cn(
                      "flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-semibold transition-all",
                      form.type === "expense"
                        ? "bg-card text-rose-600 dark:text-rose-400 shadow-sm border border-rose-500/20"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    <ArrowDownRight size={14} className="text-rose-500" />
                    <span>{t("type.expense")}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleTypeChange("income")}
                    className={cn(
                      "flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-semibold transition-all",
                      form.type === "income"
                        ? "bg-card text-emerald-600 dark:text-emerald-400 shadow-sm border border-emerald-500/20"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    <ArrowUpRight size={14} className="text-emerald-500" />
                    <span>{t("type.income")}</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleTypeChange("transfer")}
                    className={cn(
                      "flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-semibold transition-all",
                      form.type === "transfer"
                        ? "bg-card text-primary shadow-sm border border-primary/20"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    <Repeat size={14} className="text-primary" />
                    <span>{t("type.transfer")}</span>
                  </button>
                </div>
              </div>

              {/* Amount Hero Input */}
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1.5">
                  {t("txModal.amount")}
                </label>
                <div className="relative flex items-center bg-card border-2 border-border focus-within:border-primary focus-within:ring-4 focus-within:ring-primary/15 rounded-xl px-4 py-2.5 transition-all shadow-xs">
                  <span className="text-xl font-bold font-mono text-muted-foreground/80 mr-2 select-none">
                    {symbol}
                  </span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    autoFocus
                    value={form.amount}
                    onChange={(e) => set("amount", e.target.value)}
                    className="w-full bg-transparent text-2xl font-bold font-mono text-foreground placeholder:text-muted-foreground/30 focus:outline-none"
                  />
                </div>
              </div>

              {/* 2-Column Grid: Date & Person */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                {/* Date */}
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1.5 flex items-center gap-1">
                    <Calendar size={11} className="opacity-70" />
                    <span>{t("txModal.date")}</span>
                  </label>
                  <input
                    type="date"
                    value={form.date}
                    onChange={(e) => set("date", e.target.value)}
                    className="app-input text-xs font-medium"
                  />
                </div>

                {/* Person / Member */}
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1.5 flex items-center gap-1">
                    <User size={11} className="opacity-70" />
                    <span>{t("txModal.person")}</span>
                  </label>
                  <div className="space-y-1.5">
                    <input
                      type="text"
                      placeholder={t("txModal.personPh")}
                      value={
                        isKnownPersonPreset(form.person, t)
                          ? personLabel(form.person, t)
                          : form.person
                      }
                      onChange={(e) => set("person", e.target.value)}
                      className="app-input text-xs"
                    />
                    {/* Quick Presets — store English canonical, show translated */}
                    <div className="flex gap-1">
                      {personPresets.map((p) => (
                        <button
                          key={p.value}
                          type="button"
                          onClick={() => set("person", p.value)}
                          className={cn(
                            "px-2 py-0.5 rounded text-[10px] font-medium transition-colors border",
                            personCanonical(form.person, t) === p.value
                              ? "bg-primary/10 border-primary/30 text-primary"
                              : "border-border/60 hover:bg-muted text-muted-foreground"
                          )}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Category & Account */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                {/* Category */}
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1.5 flex items-center gap-1">
                    <FolderClosed size={11} className="opacity-70" />
                    <span>{t("txModal.category")}</span>
                  </label>
                  <select
                    value={form.categoryId}
                    onChange={(e) => set("categoryId", e.target.value)}
                    className="app-input text-xs font-medium"
                  >
                    <option value="" disabled>
                      {t("txModal.chooseCategory")}
                    </option>
                    {relevantCats.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.icon} {categoryLabel(c, t)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Account */}
                <div>
                  <label className="block text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1.5 flex items-center gap-1">
                    <CreditCard size={11} className="opacity-70" />
                    <span>{t("tx.account")}</span>
                  </label>
                  {accounts.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-border bg-muted/30 px-3 py-2.5 text-xs text-muted-foreground">
                      {t("empty.tx.noAccount")}
                    </div>
                  ) : (
                    <select
                      value={form.accountId}
                      onChange={(e) => set("accountId", e.target.value)}
                      className="app-input text-xs font-medium"
                    >
                      <option value="" disabled>
                        {t("tx.chooseAccount")}
                      </option>
                      {accounts.map((a) => (
                        <option key={a.id} value={a.id}>
                          💳 {a.name} ({accountTypeLabel(a.type, t)})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1.5 flex items-center gap-1">
                  <Tag size={11} className="opacity-70" />
                  <span>{t("txModal.notes")}</span>
                </label>
                <textarea
                  rows={3}
                  placeholder={t("txModal.notesPh")}
                  value={form.notes}
                  onChange={(e) => set("notes", e.target.value)}
                  className="app-input text-xs min-h-[4.5rem] resize-y"
                />
              </div>

              {/* Recurring */}
              {!initialData && (
                <div className="rounded-xl border border-border/70 p-3 space-y-2.5 bg-muted/20">
                  <label className="flex items-center gap-2 text-xs font-medium cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.recurring}
                      onChange={(e) => set("recurring", e.target.checked)}
                      className="rounded border-border"
                    />
                    <Repeat size={13} className="text-primary" />
                    {t("txModal.recurring")}
                  </label>
                  {form.recurring && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <select
                        className="app-input text-xs"
                        value={form.frequency}
                        onChange={(e) => set("frequency", e.target.value)}
                      >
                        <option value="weekly">{t("txModal.freq.weekly")}</option>
                        <option value="monthly">{t("txModal.freq.monthly")}</option>
                        <option value="custom">{t("txModal.freq.custom")}</option>
                      </select>
                      {form.frequency === "custom" && (
                        <input
                          type="number"
                          min={1}
                          className="app-input text-xs"
                          value={form.intervalDays}
                          onChange={(e) => set("intervalDays", e.target.value)}
                          placeholder={t("txModal.intervalDays")}
                        />
                      )}
                      <p className="text-[10px] text-muted-foreground sm:col-span-2">
                        {t("txModal.recurringHelp")}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {initialData?.seriesId && (
                <div className="rounded-xl border border-border/70 p-3 space-y-2 bg-muted/20">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                    {t("txModal.series")}
                  </p>
                  <div className="flex flex-col gap-1.5">
                    <label className="flex items-center gap-2 text-xs">
                      <input
                        type="radio"
                        name="scope"
                        checked={form.seriesScope === "one"}
                        onChange={() => set("seriesScope", "one")}
                      />
                      {t("txModal.scopeOne")}
                    </label>
                    <label className="flex items-center gap-2 text-xs">
                      <input
                        type="radio"
                        name="scope"
                        checked={form.seriesScope === "series"}
                        onChange={() => set("seriesScope", "series")}
                      />
                      {t("txModal.scopeSeries")}
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={() => void handleSkipOccurrence()}
                    className="text-xs text-amber-700 dark:text-amber-400 underline"
                  >
                    {t("txModal.skip")}
                  </button>
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="flex items-center justify-end gap-2.5 px-6 py-4 border-t border-border/60 bg-muted/20">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-xs font-medium rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors"
              >
                {t("common.cancel")}
              </button>

              <button
                type="submit"
                disabled={!canSave || busy}
                className={cn(
                  "px-5 py-2 text-xs font-semibold rounded-lg bg-primary text-primary-foreground shadow-sm hover:opacity-90 active:scale-[0.98] transition-all flex items-center gap-1.5",
                  (!canSave || busy) && "opacity-40 cursor-not-allowed"
                )}
              >
                {busy ? (
                  <span>{t("txModal.saving")}</span>
                ) : (
                  <>
                    <Plus size={14} />
                    <span>
                      {initialData ? t("txModal.update") : t("txModal.save")}
                    </span>
                  </>
                )}
              </button>
            </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
